/**
 * FindingPhotos — ЗӨРЧЛИЙН ЗУРАГ хавсаргах / харах.
 *
 * ⚠ ЯАГААД ХЭРЭГТЭЙ БОЛОВ (2026-08-14):
 *   `xab.inspection_file` хүснэгт, `POST /xab/inspections/file` endpoint,
 *   `xabInspectionApi.addFile` клиент — гурвуулаа БАЙСАН. Гэтэл тэдгээрийг
 *   дуудаж байгаа ДЭЛГЭЦ огт байгаагүй. Улмаас зөрчилд зураг хавсаргах
 *   боломжгүй, хэвлэсэн хуудасны «зураг баримт» хэсэг үргэлж хоосон
 *   гардаг байв.
 *
 * ⚠ ЗУРАГ НЬ МӨРД холбогдоно (`item_result_id`). Шалгалтад ерөнхийгөөр
 *   хавсаргавал хэвлэхэд аль зөрчлийнх нь мэдэгдэхгүй — цаасан баримт
 *   дээр «энэ зураг юуны тухай вэ» гэдэг нь заавал тодорхой байх ёстой.
 *
 * ⚠ КАМЕР ба ЗУРГИЙН САН — ТУСДАА ХОЁР INPUT (2026-10-03):
 *   Шалгалт хийж байгаа хүн гар утсаараа вэб рүү ордог. Урьд нь antd
 *   `<Upload>` нэг л оролттой, `capture` ч `multiple` ч байгаагүй тул
 *   (а) талбар дээр зөрчлөө ШУУД зураг авах боломжгүй, (б) өмнө авсан
 *   зургуудаасаа зөвхөн НЭГИЙГ л сонгож, дараа нь дахин дарж байх
 *   шаардлагатай байв.
 *
 *   `capture` ба `multiple`-ийг НЭГ input дээр зэрэг тавьж БОЛОХГҮЙ:
 *   нэг камерын агшин = нэг файл учир зарим Android/iOS хөтөч сонгогчийг
 *   огт нээхгүй болдог. Мөн `capture` байхад мобайл хөтөч галерейг огт
 *   санал болгохоо больдог. Иймд:
 *       cameraRef  — камер, НЭГ зураг (`capture="environment"`)
 *       galleryRef — зургийн сан, ОЛОН зураг (`capture` БАЙХГҮЙ)
 *   Энэ нь `components/hazard/PhotoSection.js`-д аль хэдийн нэг удаа
 *   шийдэгдсэн, production-д ажиллаж байгаа хэв маяг — давтаж бичив.
 */
import React, { useRef, useState } from "react";
import { Button, Image, Tooltip, message } from "antd";

import { uploadHazardPhoto } from "src/api/hazard";
import { xabInspectionApi } from "src/api/xabApi";
import { guardFile } from "src/components/MediaUpload";

export default function FindingPhotos({
  inspectionId,
  itemResultId,
  files = [],
  disabled = false,
  onChanged,
}) {
  const [busy, setBusy] = useState(false);
  // Харагч нээлттэй эсэх — хаалттай үед зураг ОГТ татагдахгүй
  const [viewer, setViewer] = useState(false);
  // Багцаар байршуулж байхад «3/8» гэж харуулна — сул сүлжээтэй талбар
  // дээр ажилтан хэр үлдсэнээ мэдэхгүй бол дахин дахин дардаг.
  const [progress, setProgress] = useState(null);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const mine = files.filter(
    (f) => String(f.item_result_id || "") === String(itemResultId),
  );

  /**
   * Сонгосон файлуудыг ДАРААЛЛААР байршуулна.
   *
   * ⚠ Зэрэг (Promise.all) илгээхгүй: талбарын сүлжээ сул, 8 зураг зэрэг
   *   илгээвэл timeout-д (60s) бүгд унах магадлалтай. Мөн нэг нь унасан
   *   ч бусад нь орсон байх нь дээр.
   */
  const handlePick = async (e) => {
    const picked = Array.from(e.target.files || []);
    // ⚠ input-ийг ЭРТ цэвэрлэнэ: ижил зургийг дахин сонгоход `change`
    //   дахин ажиллах ёстой (ижил value бол хөтөч event гаргахгүй).
    e.target.value = "";
    if (!picked.length) return;

    if (!inspectionId || !itemResultId) {
      message.warning("Эхлээд шалгалтаа хадгална уу");
      return;
    }

    setBusy(true);
    let ok = 0;
    let failed = 0;
    try {
      for (let i = 0; i < picked.length; i += 1) {
        const file = picked[i];
        setProgress(picked.length > 1 ? `${i + 1}/${picked.length}` : null);
        // Хэт том зурагт анхааруулна (components/MediaUpload.js). Энэ нь
        // ХОРИГ биш — хэрэглэгч «болих» гэвэл л тэр файлыг өнгөрүүлнэ.
        // eslint-disable-next-line no-await-in-loop
        if (!(await guardFile(file))) continue;
        try {
          // ⚠ Зургийг MinIO руу аюулын модультай ИЖИЛ замаар байршуулна —
          //   тусдаа upload endpoint нэмэх шаардлагагүй, нэг л бодлого
          //   (хэмжээ, төрлийн шалгалт) үйлчилнэ.
          // eslint-disable-next-line no-await-in-loop
          const up = await uploadHazardPhoto(file);
          // ⚠ Backend нь `file_url` гэж буцаана (Resources/mobile/upload.py).
          //   Бусад нэрийг НӨӨЦ болгож үлдээв — өөр upload зам нэмэгдвэл.
          const url = up?.file_url || up?.url || up?.photo_url;
          if (!url) throw new Error("Хаяг ирсэнгүй");
          // eslint-disable-next-line no-await-in-loop
          await xabInspectionApi.addFile({
            inspection_id: inspectionId,
            item_result_id: itemResultId,
            file_name: file.name,
            file_url: url,
            file_type: file.type || "image",
          });
          ok += 1;
        } catch (err) {
          failed += 1;
          // ⚠ Нэг зураг унасан ч ЦИКЛЭЭ ТАСАЛАХГҮЙ — 8 зургийн 2-т алдаа
          //   гарахад бусад 6 нь орох ёстой. Тоог төгсгөлд нэг дор хэлнэ.
        }
      }
    } finally {
      setBusy(false);
      setProgress(null);
    }

    if (ok) {
      message.success(ok > 1 ? `${ok} зураг хавсаргалаа` : "Зураг хавсаргалаа");
      // ⚠ onChanged-ыг БАГЦЫН ТӨГСГӨЛД нэг л удаа — файл тутамд дуудвал
      //   жагсаалт N удаа дахин татагдана.
      onChanged?.();
    }
    if (failed) {
      message.error(
        failed > 1
          ? `${failed} зураг хавсаргаж чадсангүй`
          : "Зураг хавсаргаж чадсангүй",
      );
    }
  };

  const remove = async (f) => {
    setBusy(true);
    try {
      await xabInspectionApi.deleteFile(f.id);
      message.success("Устгалаа");
      onChanged?.();
    } catch (e) {
      message.error(e?.response?.data?.message || "Устгаж чадсангүй");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      {/* ⚠ ЗУРАГ ОГТ ТАТАХГҮЙ — icon + тоо (2026-10-16-ны гацалт).
          `MEDIA_PROXY_MODE=app` тул зураг бүр Flask-ийн worker эзэлдэг
          (gunicorn 4×4 = 16 оролт). Хэмжсэн: эх зураг 3.93 MB,
          Flask-аар дамжуулахад TIMEOUT 30с (MinIO руу шууд 0.37с).
          Зөрчилд 2-3 зурагтай хуудас нээхэд API бүхэлдээ гацдаг байв.
          ⚠ `display:none` ХАНГАЛТГҮЙ — хөтөч нуусан зургийг ч ТАТДАГ.
            Тиймээс харагчийг ДАРСНЫ ДАРАА л render хийнэ. */}
      {mine.length ? (
        <span
          onClick={() => setViewer(true)}
          title={`${mine.length} зураг — харахын тулд дарна`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 8px",
            border: "1px solid #CBD5E1",
            borderRadius: 999,
            cursor: "pointer",
            background: "#F8FAFC",
          }}
        >
          <i className="fas fa-image" style={{ color: "#5A6478" }} />
          <b style={{ fontSize: 11, color: "#5A6478" }}>{mine.length}</b>
        </span>
      ) : null}

      {viewer ? (
        <Image.PreviewGroup
          preview={{
            visible: true,
            onVisibleChange: (v) => !v && setViewer(false),
          }}
        >
          {mine.map((f) => (
            <Image
              key={f.id}
              src={f.file_url}
              alt={f.file_name || ""}
              style={{ display: "none" }}
            />
          ))}
        </Image.PreviewGroup>
      ) : null}

      {/* Устгах — зураггүй, дугаартай товчоор */}
      {!disabled && mine.length ? (
        <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
          {mine.map((f, i) => (
            <button
              key={f.id}
              type="button"
              title={`${i + 1}-р зургийг устгах`}
              onClick={() => remove(f)}
              style={{
                border: "1px solid #FCA5A5",
                background: "#FEF2F2",
                color: "#B91C1C",
                borderRadius: 4,
                fontSize: 10,
                padding: "0 5px",
                cursor: "pointer",
              }}
            >
              {i + 1} ✕
            </button>
          ))}
        </span>
      ) : null}

      {!disabled ? (
        <>
          {/* ХОЁР ТОВЧ. Гар утсан дээр аль алин нь хэрэгтэй: талбар дээр
              зөрчлөө ШУУД авах, эсвэл өмнө авсан зургуудаасаа хавсаргах.
              Компьютер дээр «Камер» нь вэбкамер/файл сонгогч нээнэ. */}
          <Button
            size="small"
            loading={busy}
            onClick={() => cameraRef.current?.click()}
          >
            <i className="fas fa-camera" style={{ marginRight: 5 }} />
            {busy && progress ? progress : "Камер"}
          </Button>
          <Tooltip title="Зургийн сангаас ОЛОН зураг зэрэг сонгож болно — хэвлэсэн хуудсанд гарна">
            <Button
              size="small"
              disabled={busy}
              onClick={() => galleryRef.current?.click()}
            >
              <i className="fas fa-images" style={{ marginRight: 5 }} />
              Сангаас
            </Button>
          </Tooltip>

          {/* КАМЕР — `capture` нь ард талын камерыг шууд нээнэ. `multiple`
              ТАВИХГҮЙ (дээрх тайлбар). */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={handlePick}
          />
          {/* ЗУРГИЙН САН — `capture` БАЙХГҮЙ тул галерей нээгдэж, олон
              зураг сонгож болно. Компьютер дээр ердийн файл сонгогч. */}
          <input
            ref={galleryRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handlePick}
          />
        </>
      ) : null}

      {!mine.length && disabled ? (
        <span style={{ fontSize: 11, color: "#C2C8D2" }}>зураггүй</span>
      ) : null}
    </div>
  );
}
